import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Address from "./Address";
import { deepFreeze } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const ITEM_LOCATION_FIELDS = {
  id: null,
  bin: null,
  address: null,
  type: null,
  itemType: null,
  quantity: 0,
};
deepFreeze(ITEM_LOCATION_FIELDS);

export const ITEM_LOCATION_TYPES = [
  {
    icon: "warehouse",
    label: "Warehouse",
    value: "warehouse",
  },
  {
    icon: "vehicle",
    label: "Vehicle",
    value: "vehicle",
  },
  {
    icon: "temporary_destination",
    label: "Temporary Destination",
    value: "temp",
  },
  {
    icon: "done",
    label: "Final Destination",
    value: "final",
  },
];
deepFreeze(ITEM_LOCATION_TYPES);

export const ITEM_LOCATION_ITEM_TYPES = [
  {
    icon: "window",
    label: "Full Unit",
    value: "unit",
  },
  {
    icon: "hardware",
    label: "Hardware",
    value: "hardware",
  },
  {
    icon: "screens",
    label: "Screen",
    value: "screen",
  },
  {
    icon: "accessory",
    label: "Accessory",
    value: "accessory",
  },
  {
    icon: "service",
    label: "Service Part",
    value: "part",
  },
];
deepFreeze(ITEM_LOCATION_ITEM_TYPES);

const HIDDEN_WAREHOUSE_PREFIXES = ["A", "B", "B Screen Lofts"];
deepFreeze(HIDDEN_WAREHOUSE_PREFIXES);

export function normalizeItemLocationBin(bin) {
  if (bin === "null" || bin === null || bin === undefined || bin === "") {
    return null;
  }
  const number = Number(bin);
  if (Number.isInteger(number)) {
    return number;
  }
  return bin;
}

export function normalizeItemLocationData(data = {}) {
  return sanitize(
    {
      ...data,
      bin: normalizeItemLocationBin(data.bin),
    },
    ITEM_LOCATION_FIELDS
  );
}

function itemLocationKey(location = {}) {
  return [
    location.type || "",
    location.id || "",
    location.itemType || "",
    location.bin ?? "",
    location.address ? JSON.stringify(location.address) : "",
  ].join("|");
}

export function dedupeItemLocations(locations = []) {
  const deduped = new Map();
  locations.forEach((location) => {
    const normalized = normalizeItemLocationData(location);
    if (!(normalized.quantity > 0)) {
      return;
    }
    const key = itemLocationKey(normalized);
    const existing = deduped.get(key);
    if (existing) {
      existing.quantity += normalized.quantity;
    } else {
      deduped.set(key, normalized);
    }
  });
  return Array.from(deduped.values());
}

export function normalizeItemLocationHistory(itemLocations = {}) {
  return Object.keys(itemLocations || {}).reduce((normalized, key) => {
    normalized[key] = dedupeItemLocations(itemLocations[key] || []);
    return normalized;
  }, {});
}

/**
 * Return the latest snapshot (the current physical state) from an
 * `itemLocations` history map, which is keyed by timestamp in ms.
 */
export function latestItemLocations(itemLocations = {}) {
  const keys = Object.keys(itemLocations || {}).sort();
  if (!keys.length) return [];
  const latest = itemLocations[keys[keys.length - 1]];
  return Array.isArray(latest) ? latest : [];
}

/**
 * Quantity currently sitting at a temporary destination, derived from the
 * latest `itemLocations` snapshot. Mirrors the backend
 * `computeAtTemporaryDestination` in functions/items.js.
 */
export function temporaryQuantity(itemLocations = {}) {
  return latestItemLocations(itemLocations).reduce(
    (sum, location) =>
      sum +
      (location && location.type === "temp"
        ? Number(location.quantity) || 0
        : 0),
    0
  );
}

export default class ItemLocation extends DataStructure {
  constructor(data = {}) {
    super(normalizeItemLocationData(data));
  }

  // Editing

  get hasBin() {
    // Bin-bearing zones are derived reactively from the active warehouse maps
    // (falling back to the bundled constants until they're seeded).
    const withBins = store.getters?.warehouseLocationsWithBins || [];
    return this.data.id && withBins.includes(this.data.id);
  }

  // Legacy/malformed data stored the zone label in `bin` as a string. A
  // numeric bin is a real bin number: `hasBin` reads the warehouse maps, so
  // it is false until they load (or for a zone missing from them), and a
  // numeric bin must not be mistaken for a zone label then.
  get hasLegacyZoneBin() {
    return typeof this.data.bin === "string" && !this.hasBin;
  }

  get isValid() {
    return (
      this.data.itemType &&
      this.data.id &&
      this.data.quantity > 0 &&
      (!this.hasBin ||
        (this.data.bin && this.data.bin >= 1 && this.data.bin <= 999))
    );
  }

  static isEmpty(data = this.data) {
    return isEmpty(diff(data, ITEM_LOCATION_FIELDS));
  }

  // Formatting

  get isDelivered() {
    return (
      this.data.type === "final" &&
      ["", "delivered", "installed"].includes(this.data.id)
    );
  }

  get isInstalled() {
    return this.data.type === "final" && this.data.id === "installed";
  }

  get isTemporary() {
    return this.data.type === "temp";
  }

  get isVehicle() {
    return this.data.type === "vehicle";
  }

  get isWarehouseZone() {
    return this.data.type === "warehouse";
  }

  get vehicle() {
    return this.isVehicle
      ? store.state.main.vehicles[this.data.id] || null
      : null;
  }

  // Stable identity for grouping/filtering/deduping. Unlike `name`/`toString`
  // this never reads the vehicles store, so it stays constant while the
  // display label resolves once the vehicles listener delivers its snapshot.
  // Anything that persists or compares a location must use this, not the label.
  get key() {
    if (this.data.id === null) {
      return "";
    }
    const base = `${this.data.type}:${this.data.id}`;
    return this.hasBin && this.data.bin ? `${base}:${this.data.bin}` : base;
  }

  get name() {
    if (this.data.id === null) {
      return null;
    } else if (this.isInstalled) {
      return i18n.global.t("Installed");
    } else if (this.isDelivered) {
      return i18n.global.t("Delivered");
    } else if (this.isTemporary) {
      return i18n.global.t("Temporary Destination");
    } else if (this.isWarehouseZone) {
      // Handle legacy/malformed data where zone was stored as bin
      if (this.hasLegacyZoneBin) {
        return this.data.bin;
      }
      return this.zone + (this.hasBin && this.data.bin ? this.data.bin : "");
    } else if (this.isVehicle) {
      return this.vehicle ? this.vehicle.toString() : "...";
    } else {
      return i18n.global.t("Unknown");
    }
  }

  get zoneIcon() {
    if (this.data.id === null) {
      return "";
    } else if (this.isInstalled) {
      return "installation";
    } else if (this.isDelivered) {
      return "delivered";
    } else if (this.isTemporary) {
      return "temporary_destination";
    } else if (this.isWarehouseZone) {
      return "warehouse";
    } else if (this.isVehicle) {
      const vehicle = this.vehicle;
      return vehicle && vehicle.type ? vehicle.type.icon : "vehicle";
    } else {
      return "unknown";
    }
  }

  get type() {
    return this.data.type
      ? ITEM_LOCATION_TYPES.find((t) => t.value === this.data.type)
      : null;
  }

  get itemType() {
    return this.data.itemType
      ? ITEM_LOCATION_ITEM_TYPES.find((t) => t.value === this.data.itemType)
      : null;
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : null;
  }

  get warehouse() {
    return this.data.id && this.isWarehouseZone
      ? this.data.id.split(":")[0]
      : "";
  }

  get zone() {
    if (!this.data.id || !this.isWarehouseZone) {
      return "";
    }
    // Handle legacy/malformed data where zone was stored as bin
    if (this.hasLegacyZoneBin) {
      return this.data.bin.split(":")[0];
    }
    const parts = this.data.id.split(":");
    return parts.length > 1 ? parts[1] : parts[0];
  }

  get warehouseName() {
    const name = this.toString();
    if (
      !this.isWarehouseZone ||
      !this.warehouse ||
      HIDDEN_WAREHOUSE_PREFIXES.includes(this.warehouse)
    ) {
      return null;
    }

    return this.warehouse;
  }

  get displayNameWithWarehouse() {
    const name = this.toString();
    if (
      !this.isWarehouseZone ||
      !this.warehouse ||
      HIDDEN_WAREHOUSE_PREFIXES.includes(this.warehouse)
    ) {
      return name;
    }

    return `${this.warehouse}:${name}`;
  }

  toString() {
    return this.name || "";
  }
}
